/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { state } from '@/quote-management/lib/state';
import { UPDATE_NEGOTIABLE_QUOTE_QUANTITIES_MUTATION } from './graphql/UpdateNegotiableQuoteQuantitiesMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';

export interface QuantityItem {
  /** The unique ID of the quote item */
  quoteItemUid: string;
  /** The new quantity for the item (must be greater than 0) */
  quantity: number;
}

export interface UpdateQuantitiesInput {
  /** The unique ID of the negotiable quote */
  quoteUid: string;
  /** Array of items with their new quantities */
  items: QuantityItem[];
}

/**
 * Updates the quantities of items in a negotiable quote.
 *
 * @param input - The input parameters for updating quantities
 * @param input.quoteUid - The unique ID of the negotiable quote
 * @param input.items - Array of items with their new quantities
 * @returns Promise that resolves to the updated quote model
 * @throws Error if validation fails or GraphQL operation fails
 *
 * @example
 * ```ts
 * const quote = await updateQuantities({
 *   quoteUid: 'quote-123',
 *   items: [
 *     { quoteItemUid: 'item-1', quantity: 5 },
 *     { quoteItemUid: 'item-2', quantity: 3 }
 *   ]
 * });
 * ```
 */
export const updateQuantities = async (
  input: UpdateQuantitiesInput
): Promise<NegotiableQuoteModel | null> => {
  if (!state.authenticated) {
    return Promise.reject(new Error('Unauthorized'));
  }

  const { quoteUid, items } = input;

  // Validate quoteUid
  if (!quoteUid) {
    throw new Error('Quote UID is required');
  }

  // Validate items array
  if (!items || !Array.isArray(items) || items.length === 0) {
    throw new Error('Items array is required and must not be empty');
  }

  // Validate each item
  for (const item of items) {
    if (!item.quoteItemUid) {
      throw new Error('Each item must have a quoteItemUid');
    }
    if (typeof item.quantity !== 'number' || item.quantity <= 0) {
      throw new Error(
        `Invalid quantity for item ${item.quoteItemUid}: quantity must be a positive number`
      );
    }
    // Ensure quantity is an integer
    if (!Number.isInteger(item.quantity)) {
      throw new Error(
        `Invalid quantity for item ${item.quoteItemUid}: quantity must be an integer`
      );
    }
  }

  // Transform items to snake_case for GraphQL
  const transformedItems = items.map((item) => ({
    quote_item_uid: item.quoteItemUid,
    quantity: item.quantity,
  }));

  return fetchGraphQl(UPDATE_NEGOTIABLE_QUOTE_QUANTITIES_MUTATION, {
    variables: {
      quoteUid,
      items: transformedItems,
    },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      // Provide more detailed error information
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to update quote quantities: ${errorMessages}`);
    }

    // Transform the quote data
    const quoteData = transformQuote(
      response.data?.updateNegotiableQuoteQuantities?.quote
    );

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    // Emit event with transformed quote data on success
    events.emit('quote-management/quantities-updated', {
      quote: quoteData,
      input: { quoteUid, items },
    });

    return quoteData;
  });
};
